import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const COLUMNS = [
    { key: 'EVENT', label: 'Event' },
    { key: 'FIGHTER', label: 'Fighter' },
    { key: 'WEIGHT CLASS', label: 'Weight Class' },
    { key: 'WEIGH IN WEIGHT (lbs)', label: 'Weigh In', numeric: true },
    { key: 'FIGHT NIGHT WEIGHT (lbs)', label: 'Fight Night', numeric: true },
    { key: 'WEIGHT INCREASE (lbs)', label: 'Diff.', numeric: true },
    { key: 'PERCENTAGE REGAINED', label: 'Rel. Diff.', numeric: true }
];

const FILTERABLE_COLUMNS = ['WEIGHT CLASS'];

function ReboundsViewer() {
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortBy, setSortBy] = useState('PERCENTAGE REGAINED');
    const [sortDir, setSortDir] = useState('DESC');
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({});
    const [uniqueValues, setUniqueValues] = useState({});
    const [showFilters, setShowFilters] = useState({});
    const [useMetric, setUseMetric] = useState(false);
    const [weightClassStats, setWeightClassStats] = useState([]);
    const [displayLimit, setDisplayLimit] = useState(10);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        applyFiltersAndSort();
    }, [rows, sortBy, sortDir, search, filters]);

    useEffect(() => {
        calculateWeightClassStats();
    }, [rows, useMetric]); // Calculate from all rows, not filtered rows

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/rebounds`);
            if (!res.ok) throw new Error('Failed to fetch rebounds data');
            const data = await res.json();
            setRows(data);
            
            // Extract unique values for filters and sort weight classes
            const uniques = {};
            FILTERABLE_COLUMNS.forEach(col => {
                const values = [...new Set(data.map(row => row[col]).filter(v => v !== null && v !== ''))];
                
                if (col === 'WEIGHT CLASS') {
                    // Sort weight classes heaviest to lightest
                    const menOrder = ['Heavyweight', 'Light Heavyweight', 'Middleweight', 'Welterweight', 'Lightweight', 'Featherweight', 'Bantamweight', 'Flyweight'];
                    const womenOrder = ["Women's Featherweight", "Women's Bantamweight", "Women's Flyweight", "Women's Strawweight"];
                    const order = [...menOrder, ...womenOrder];
                    
                    values.sort((a, b) => {
                        const aIndex = order.indexOf(a);
                        const bIndex = order.indexOf(b);
                        if (aIndex === -1 && bIndex === -1) return 0;
                        if (aIndex === -1) return 1;
                        if (bIndex === -1) return -1;
                        return aIndex - bIndex;
                    });
                } else {
                    values.sort();
                }
                
                uniques[col] = values;
            });
            setUniqueValues(uniques);
        } catch (e) {
            console.error('Error loading rebounds data:', e);
            alert('Failed to load data. Please ensure the server is running.');
        } finally {
            setLoading(false);
        }
    };

    const applyFiltersAndSort = () => {
        let filtered = [...rows];

        // Apply search
        if (search.trim()) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(row => 
                Object.values(row).some(val => 
                    val?.toString().toLowerCase().includes(searchLower)
                )
            );
        }

        // Apply filters
        Object.entries(filters).forEach(([col, values]) => {
            if (values && values.length > 0) {
                filtered = filtered.filter(row => values.includes(row[col]));
            }
        });

        // Apply sorting
        if (sortBy) {
            filtered.sort((a, b) => {
                let aVal = a[sortBy];
                let bVal = b[sortBy];

                // Handle numeric columns
                const numericCols = ['WEIGH IN WEIGHT (lbs)', 'FIGHT NIGHT WEIGHT (lbs)', 'WEIGHT INCREASE (lbs)', 'PERCENTAGE REGAINED'];
                if (numericCols.includes(sortBy)) {
                    aVal = parseFloat(aVal) || 0;
                    bVal = parseFloat(bVal) || 0;
                } else {
                    aVal = aVal?.toString().toLowerCase() || '';
                    bVal = bVal?.toString().toLowerCase() || '';
                }

                if (aVal < bVal) return sortDir === 'ASC' ? -1 : 1;
                if (aVal > bVal) return sortDir === 'ASC' ? 1 : -1;
                return 0;
            });
        }

        setFilteredRows(filtered);
    };

    const toggleSort = (column) => {
        if (sortBy === column) {
            setSortDir(sortDir === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(column);
            setSortDir('DESC');
        }
    };

    const toggleFilter = (column, value) => {
        setFilters(prev => {
            const current = prev[column] || [];
            const updated = current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value];
            return { ...prev, [column]: updated };
        });
    };

    const clearAllFilters = () => {
        setFilters({});
        setSearch('');
    };

    const toggleFilterDropdown = (column) => {
        setShowFilters(prev => ({ ...prev, [column]: !prev[column] }));
    };

    const toggleUnits = () => {
        setUseMetric(!useMetric);
    };

    const convertWeight = (lbs) => {
        if (!lbs) return null;
        return useMetric ? (lbs / 2.20462).toFixed(1) : parseFloat(lbs).toFixed(1);
    };

    const getWeightUnit = () => useMetric ? 'kg' : 'lbs';

    const calculateWeightClassStats = () => {
        const statsByClass = {};
        
        rows.forEach(row => { // Use all rows, not filtered
            const wc = row['WEIGHT CLASS'];
            if (!wc) return;
            
            if (!statsByClass[wc]) {
                statsByClass[wc] = {
                    totalPercentage: 0,
                    totalWeightGain: 0,
                    count: 0
                };
            }
            
            statsByClass[wc].totalPercentage += parseFloat(row['PERCENTAGE REGAINED']) || 0;
            statsByClass[wc].totalWeightGain += parseFloat(row['WEIGHT INCREASE (lbs)']) || 0;
            statsByClass[wc].count += 1;
        });

        // Define weight class order (heaviest to lightest)
        const menOrder = ['Heavyweight', 'Light Heavyweight', 'Middleweight', 'Welterweight', 'Lightweight', 'Featherweight', 'Bantamweight', 'Flyweight'];
        const womenOrder = ["Women's Featherweight", "Women's Bantamweight", "Women's Flyweight", "Women's Strawweight"];
        const order = [...menOrder, ...womenOrder];

        const stats = Object.entries(statsByClass)
            .map(([weightClass, data]) => ({
                weightClass,
                avgPercentage: (data.totalPercentage / data.count).toFixed(2),
                avgWeightGain: (data.totalWeightGain / data.count).toFixed(1),
                count: data.count,
                isWomen: weightClass.includes("Women's")
            }))
            .filter(stat => stat.weightClass !== 'Catchweight') // Remove catchweight
            .sort((a, b) => {
                const aIndex = order.indexOf(a.weightClass);
                const bIndex = order.indexOf(b.weightClass);
                if (aIndex === -1 && bIndex === -1) return 0;
                if (aIndex === -1) return 1;
                if (bIndex === -1) return -1;
                return aIndex - bIndex;
            });

        setWeightClassStats(stats);
    };

    const styles = {
        container: {
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '3rem 4rem'
        },
        header: {
            marginBottom: '2rem'
        },
        title: {
            fontSize: '2rem',
            fontWeight: 700,
            marginBottom: '0.5rem',
            color: '#fff'
        },
        subtitle: {
            fontSize: '1rem',
            color: '#aaa',
            marginBottom: '1rem'
        },
        infoBox: {
            background: 'rgba(111, 179, 224, 0.1)',
            border: '1px solid rgba(111, 179, 224, 0.3)',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
        },
        infoTitle: {
            fontSize: '1.1rem',
            fontWeight: 600,
            color: '#6fb3e0',
            marginBottom: '0.75rem'
        },
        infoText: {
            fontSize: '0.95rem',
            color: '#ccc',
            lineHeight: '1.6',
            marginBottom: '0.75rem'
        },
        reminder: {
            fontSize: '0.95rem',
            color: '#fff',
            fontWeight: 600,
            fontStyle: 'italic',
            marginTop: '1rem',
            padding: '1rem',
            background: 'rgba(210, 10, 10, 0.15)',
            border: '1px solid rgba(210, 10, 10, 0.3)',
            borderRadius: '6px',
            lineHeight: '1.6'
        },
        highlightText: {
            color: '#ff6b6b',
            fontWeight: 700
        },
        controls: {
            display: 'flex',
            gap: '1rem',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            alignItems: 'center'
        },
        searchBox: {
            flex: '1 1 300px',
            padding: '0.75rem',
            fontSize: '1rem',
            border: '1px solid #555',
            borderRadius: '6px',
            background: '#222',
            color: '#fff',
            outline: 'none'
        },
        filterBtn: {
            position: 'relative'
        },
        btn: {
            padding: '0.3rem 0.7rem',
            fontSize: '0.8rem',
            fontWeight: 500,
            border: '1px solid #555',
            borderRadius: '8px',
            background: 'rgba(68, 68, 68, 0.4)',
            color: '#fff',
            cursor: 'pointer',
            transition: 'all 0.25s ease',
            whiteSpace: 'nowrap',
            backdropFilter: 'blur(10px)'
        },
        btnActive: {
            background: 'rgba(111, 179, 224, 0.25)',
            borderColor: '#6fb3e0',
            color: '#6fb3e0',
            boxShadow: '0 0 12px rgba(111, 179, 224, 0.3)'
        },
        clearBtn: {
            padding: '0.6rem 1.2rem',
            fontSize: '0.9rem',
            fontWeight: 500,
            border: '1px solid rgba(211, 47, 47, 0.5)',
            borderRadius: '8px',
            background: 'rgba(211, 47, 47, 0.15)',
            color: '#ff6b6b',
            cursor: 'pointer',
            transition: 'all 0.25s ease'
        },
        unitBtn: {
            padding: '0.6rem 1.2rem',
            fontSize: '0.9rem',
            fontWeight: 500,
            border: '1px solid #6fb3e0',
            borderRadius: '8px',
            background: 'transparent',
            color: '#6fb3e0',
            cursor: 'pointer',
            transition: 'all 0.25s ease',
            whiteSpace: 'nowrap'
        },
        unitBtnActive: {
            background: 'rgba(111, 179, 224, 0.2)',
            boxShadow: '0 0 12px rgba(111, 179, 224, 0.3)'
        },
        toggleSwitch: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.35rem 0.5rem',
            background: 'rgba(68, 68, 68, 0.4)',
            borderRadius: '20px',
            border: '1px solid #555',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.25s ease'
        },
        toggleLabel: {
            fontSize: '0.8rem',
            color: '#999',
            fontWeight: 500,
            minWidth: '24px',
            textAlign: 'center'
        },
        toggleKnob: {
            width: '32px',
            height: '20px',
            background: '#444',
            borderRadius: '10px',
            position: 'relative',
            transition: 'all 0.25s ease',
            border: '1px solid #555'
        },
        toggleKnobActive: {
            background: '#6fb3e0',
            border: '1px solid #6fb3e0'
        },
        toggleKnobCircle: {
            width: '16px',
            height: '16px',
            background: '#fff',
            borderRadius: '50%',
            position: 'absolute',
            top: '2px',
            left: '2px',
            transition: 'all 0.25s ease'
        },
        toggleKnobCircleActive: {
            left: '14px'
        },
        dropdown: {
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '0.5rem',
            background: '#222',
            border: '1px solid #555',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            zIndex: 1000,
            minWidth: '200px',
            maxHeight: '300px',
            overflowY: 'auto'
        },
        dropdownItem: {
            padding: '0.75rem 1rem',
            cursor: 'pointer',
            transition: 'background 0.15s',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.9rem'
        },
        checkbox: {
            width: '16px',
            height: '16px',
            cursor: 'pointer'
        },
        stats: {
            display: 'flex',
            gap: '1rem',
            marginBottom: '1rem',
            fontSize: '0.9rem',
            color: '#aaa'
        },
        tableWrapper: {
            overflowX: 'auto',
            background: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '8px',
            border: '1px solid #2d3748'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.8rem',
            tableLayout: 'auto'
        },
        th: {
            padding: '0.3rem 0.5rem',
            textAlign: 'left',
            background: 'rgba(0, 0, 0, 0.2)',
            color: '#e0e0e0',
            fontWeight: 600,
            position: 'sticky',
            top: 0,
            cursor: 'pointer',
            userSelect: 'none',
            borderBottom: '2px solid #2d3748',
            whiteSpace: 'nowrap',
            fontSize: '0.8rem'
        },
        td: {
            padding: '0.25rem 0.5rem',
            borderBottom: '1px solid #1a202c',
            color: '#ccc'
        },
        rowHighlight: (index) => ({
            background: index % 2 === 0 ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
            transition: 'background 0.2s ease'
        }),
        sortIcon: {
            marginLeft: '0.5rem',
            fontSize: '0.75rem',
            opacity: 0.7
        },
        loading: {
            textAlign: 'center',
            padding: '3rem',
            fontSize: '1.2rem',
            color: '#aaa'
        },
        statsSection: {
            marginTop: '4rem',
            marginBottom: '2rem',
            padding: '0',
            textAlign: 'center'
        },
        statsTitle: {
            fontSize: '0.85rem',
            fontWeight: 600,
            marginBottom: '0.75rem',
            color: '#888',
            textTransform: 'uppercase',
            letterSpacing: '1px'
        },
        statsGrid: {
            display: 'inline-flex',
            gap: '3rem',
            fontSize: '0.85rem',
            justifyContent: 'center'
        },
        statsColumn: {
            display: 'flex',
            flexDirection: 'column',
            gap: '0.35rem',
            textAlign: 'left'
        },
        statsColumnTitle: {
            fontSize: '0.75rem',
            fontWeight: 400,
            color: '#666',
            marginBottom: '0.4rem',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            borderBottom: '2px solid #333',
            paddingBottom: '0.25rem'
        },
        statLine: {
            fontSize: '0.85rem',
            color: '#ddd',
            lineHeight: '1.6'
        },
        statClass: {
            color: '#6fb3e0',
            fontWeight: 400,
            minWidth: '160px',
            display: 'inline-block'
        },
        statValue: {
            color: '#fff',
            fontWeight: 400
        },
        statDetail: {
            color: '#888',
            fontSize: '0.8rem'
        },
        statCount: {
            color: '#666',
            fontSize: '0.75rem',
            marginLeft: '0.5rem'
        }
    };

    if (loading) {
        return <div style={styles.loading}>Loading rebounds data...</div>;
    }

    const activeFilterCount = Object.values(filters).filter(arr => arr && arr.length > 0).length;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>UFC Weight Rebounds</h1>
                <p style={styles.subtitle}>Weight gains between weigh-ins and fight night across UFC events. </p>
                <div style={{fontSize: '0.9rem', color: '#999', marginTop: '0.75rem', lineHeight: '1.3', marginRight: '30rem'}}>
                    <p style={{margin: 0}}>Data is either pulled from events sanctioned by the CSAC, or self-reported (noted with an asterisk).</p>
                    <p style={{margin: 0}}>The CSAC is the only Athletic Commission that enforces collection and publication of Fight Night weight.</p>
                    <p style={{margin: 0}}>Note: Whilst most people correctly differentiate Weigh-In weight from Fight Night weight, too many somehow often confuse Fight Night weight with "walk-around weight". The two rarely coincide, and only Fight Night weight is relevant to being a "weight bully". Typical example: "walk around" at 85 kg (187 lbs), cut fat to 78 kg (172 lbs) during camp, cut water to 70 kg (155 lbs) for weigh-ins, rehydrate to 78 kg (average for LW).</p>
                </div>
            </div>

            <div style={styles.stats}>
                {activeFilterCount > 0 && <span>{activeFilterCount} filter(s) active</span>}
            </div>

            <div style={styles.controls}>
                {FILTERABLE_COLUMNS.map(col => (
                    <div key={col} style={styles.filterBtn}>
                        <button
                            style={{
                                ...styles.btn,
                                ...(filters[col]?.length > 0 ? styles.btnActive : {})
                            }}
                            onClick={() => toggleFilterDropdown(col)}
                            onMouseEnter={(e) => {
                                if (filters[col]?.length > 0) {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                } else {
                                    e.currentTarget.style.background = 'rgba(68, 68, 68, 0.7)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (filters[col]?.length > 0) {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                } else {
                                    e.currentTarget.style.background = 'rgba(68, 68, 68, 0.4)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }
                            }}
                        >
                            {COLUMNS.find(c => c.key === col)?.label}
                            {filters[col]?.length > 0 && ` (${filters[col].length})`}
                            <span style={{marginLeft: '0.5rem', fontSize: '0.7rem'}}>▼</span>
                        </button>
                        
                        {showFilters[col] && (
                            <div style={styles.dropdown}>
                                {uniqueValues[col]?.map(value => (
                                    <div
                                        key={value}
                                        style={styles.dropdownItem}
                                        onClick={() => toggleFilter(col, value)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#333'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={filters[col]?.includes(value) || false}
                                            onChange={() => {}}
                                            style={styles.checkbox}
                                        />
                                        <span>{value}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                {(activeFilterCount > 0 || search) && (
                    <button 
                        style={styles.clearBtn} 
                        onClick={clearAllFilters}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(211, 47, 47, 0.3)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(211, 47, 47, 0.15)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        Clear All
                    </button>
                )}

                <div 
                    style={{
                        ...styles.toggleSwitch,
                        background: 'rgba(68, 68, 68, 0.4)',
                        borderColor: '#555'
                    }}
                    onClick={toggleUnits}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                    }}
                >
                    <span style={{...styles.toggleLabel, color: '#999'}}>lbs</span>
                    <div style={{...styles.toggleKnob, background: '#444', border: '1px solid #555'}}>
                        <div style={{...styles.toggleKnobCircle, ...(useMetric ? styles.toggleKnobCircleActive : {})}} />
                    </div>
                    <span style={{...styles.toggleLabel, color: '#999'}}>kg</span>
                </div>

                {filteredRows.length > 0 && displayLimit === 10 && filteredRows.length > 10 && (
                    <span style={{marginLeft: 'auto', color: '#999', fontSize: '0.9rem'}}>Showing 10 of {filteredRows.length} records</span>
                )}
            </div>

            <div style={styles.tableWrapper}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            {COLUMNS.map(col => {
                                let headerLabel = col.label;
                                if (col.key === 'WEIGHT INCREASE (lbs)') {
                                    headerLabel = `Diff. (${getWeightUnit()})`;
                                } else if (col.key === 'WEIGH IN WEIGHT (lbs)') {
                                    headerLabel = `Weigh In (${getWeightUnit()})`;
                                } else if (col.key === 'FIGHT NIGHT WEIGHT (lbs)') {
                                    headerLabel = `Fight Night (${getWeightUnit()})`;
                                }
                                return (
                                    <th
                                        key={col.key}
                                        style={styles.th}
                                        onClick={() => toggleSort(col.key)}
                                    >
                                        {headerLabel}
                                        {sortBy === col.key && (
                                            <span style={styles.sortIcon}>
                                                {sortDir === 'ASC' ? '▲' : '▼'}
                                            </span>
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows.slice(0, displayLimit).map((row, idx) => (
                            <tr key={idx} style={styles.rowHighlight(idx)}>
                                {COLUMNS.map(col => {
                                    let value = row[col.key];
                                    
                                    if (col.key === 'PERCENTAGE REGAINED') {
                                        value = `${parseFloat(value).toFixed(2)}%`;
                                    } else if (col.numeric && col.key.includes('WEIGHT')) {
                                        value = value ? `${convertWeight(value)}` : '-';
                                    } else {
                                        value = value || '-';
                                    }
                                    
                                    return (
                                        <td key={col.key} style={styles.td}>
                                            {value}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {filteredRows.length > 10 && (
                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                    <button 
                        style={styles.btn}
                        onClick={() => setDisplayLimit(displayLimit === 10 ? filteredRows.length : 10)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(68, 68, 68, 0.7)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(68, 68, 68, 0.4)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        {displayLimit === 10 ? 'See All' : 'Show Less'}
                    </button>
                </div>
            )}

            {weightClassStats.length > 0 && (
                <div style={styles.statsSection}>
                    <h2 style={{ ...styles.title, marginTop: '3rem', marginBottom: '1.5rem', fontSize: '1.5rem' }}>Sample Averages by Weight Class</h2>
                    <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Weight Class</th>
                                    <th style={styles.th}>Avg. Rel. Diff.</th>
                                    <th style={styles.th}>Avg. Diff.</th>
                                    <th style={styles.th}>Sample Size</th>
                                </tr>
                            </thead>
                            <tbody>
                                {weightClassStats.map((stat, idx) => (
                                    <tr key={stat.weightClass} style={styles.rowHighlight(idx)}>
                                        <td style={{...styles.td, textAlign: 'left'}}>{stat.weightClass}</td>
                                        <td style={{...styles.td, textAlign: 'left'}}>{stat.avgPercentage}%</td>
                                        <td style={{...styles.td, textAlign: 'left'}}>
                                            {useMetric 
                                                ? (stat.avgWeightGain / 2.20462).toFixed(1) 
                                                : stat.avgWeightGain} {getWeightUnit()}
                                        </td>
                                        <td style={{...styles.td, textAlign: 'left'}}>{stat.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReboundsViewer;
